import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    inject,
    DestroyRef,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { qpxToast as toast } from '@quipex/core/utils';
import {
    QpxFileTypeIcon,
    QpxButton,
    injectFilePreviewDialog,
    QpxOverflow,
    QpxTooltip,
    QpxTypography,
    toDocumentFileNameParts,
} from '@quipex/shared/ui';
import { catchError, finalize, of } from 'rxjs';
import { QuippyApi } from '../../../../runtime';
@Component({
    selector: 'qpx-quippy-pack-assigned-document',
    imports: [
        QpxFileTypeIcon,
        MatButtonModule,
        QpxButton,
        QpxOverflow,
        QpxTooltip,
        QpxTypography,
    ],
    templateUrl: './assigned-document.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'inline-flex min-w-0 max-w-64',
    },
})
export class QuippyPackAssignedDocument {
    public readonly joined = input(false);
    public readonly disabled = input(false);
    protected readonly pending = signal(false);
    private readonly destroyRef = inject(DestroyRef);
    private readonly previewDialog = injectFilePreviewDialog();
    public readonly fileName = input.required<string>();
    public readonly buildingId = input.required<string>();
    public readonly documentId = input.required<string>();
    private readonly api = inject(QuippyApi);
    private readonly metadata = rxResource({
        params: () =>
            toDocumentFileNameParts(this.fileName()).badge
                ? undefined
                : {
                      buildingId: this.buildingId(),
                      documentId: this.documentId(),
                  },
        stream: ({ params }) =>
            this.api
                .getSourceMetadata(params.buildingId, params.documentId)
                .pipe(catchError(() => of(null))),
    });
    protected readonly parts = computed(() =>
        toDocumentFileNameParts(
            this.metadata.value()?.fileName ?? this.fileName()
        )
    );
    protected preview(): void {
        if (this.pending() || this.disabled()) return;
        this.pending.set(true);
        this.api
            .getDocumentViewUrl(this.buildingId(), this.documentId())
            .pipe(
                finalize(() => this.pending.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (target) =>
                    this.previewDialog.open({
                        previewUrl: target.viewUrl,
                        fileName: target.fileName,
                    }),
                error: () => {
                    // eslint-disable-next-line agentic/prefer-auto-error-toast -- Quippy document previews suppress the automatic HTTP toast.
                    toast.error(
                        'Failed to open the document preview. Please try again.'
                    );
                },
            });
    }
}
